import { useEffect, useRef } from 'react';

/**
 * Call the user back when a run stops and waits for them.
 *
 * A full generation runs for ten-plus minutes, so the engineer leaves. If the
 * pipeline then stops at a confirmation gate, nothing in the app says so — they
 * come back at minute fifteen and find it has been idle since minute three. That
 * is the worst failure mode this product has, and it costs nothing to fix.
 *
 * Two channels, deliberately in this order:
 *   - the tab title, which always works and needs no permission
 *   - a browser notification, when the user has granted permission
 */

const TITLE_MARKER = '●';

function notificationsAvailable(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/**
 * Ask for notification permission from inside a real user gesture.
 *
 * Browsers reject (and users resent) permission prompts that arrive unprompted,
 * so this is called when the user sends a message — the moment they commit to a
 * run that may take ten minutes. It resolves immediately once decided, so
 * calling it on every send prompts at most once.
 */
export function ensureAttentionPermission(): void {
  if (!notificationsAvailable()) return;
  if (Notification.permission !== 'default') return;
  void Notification.requestPermission().catch(() => undefined);
}

export function useAttentionRecall(active: boolean, label: string): void {
  const baseTitleRef = useRef<string>('');
  const notificationRef = useRef<Notification | null>(null);
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (!active) {
      notifiedRef.current = false;
      return undefined;
    }

    // Captured on activation so a restore never writes back a marked title.
    if (!baseTitleRef.current) {
      baseTitleRef.current = document.title.replace(new RegExp(`^${TITLE_MARKER}[^·]*·\\s*`), '');
    }

    const clearNotification = () => {
      notificationRef.current?.close();
      notificationRef.current = null;
    };

    const apply = () => {
      if (document.hidden) {
        document.title = `${TITLE_MARKER} ${label} · ${baseTitleRef.current}`;
        if (!notifiedRef.current && notificationsAvailable() && Notification.permission === 'granted') {
          notifiedRef.current = true;
          try {
            notificationRef.current = new Notification(label, { tag: 'realplc-attention' });
          } catch {
            // Notification construction fails on some platforms (mobile Chrome
            // requires a service worker). The title marker still stands.
          }
        }
        return;
      }
      document.title = baseTitleRef.current;
      clearNotification();
    };

    apply();
    document.addEventListener('visibilitychange', apply);

    return () => {
      document.removeEventListener('visibilitychange', apply);
      if (baseTitleRef.current) document.title = baseTitleRef.current;
      clearNotification();
    };
  }, [active, label]);
}
